// src/hooks/common/useTable.ts
import {useSelector, useDispatch} from "react-redux";
import {useCallback, useMemo} from "react";
import {
  selectCurrentPage,
  selectPageSize,
  selectSortConfig,
  selectFilters,
  setCurrentPage,
  setPageSize,
  setSortConfig,
  clearSortConfig,
  setFilters,
  updateFilter,
  clearFilters,
  clearFilter,
} from "../../store/slices/uiSlice";
import {DEFAULT_PAGINATION} from "../../utils/constants";
import type {SortDirection} from "../../types";

interface UseTableOptions {
  key: string;
  defaultPageSize?: number;
  defaultSortKey?: string;
  defaultSortDirection?: SortDirection;
}

export const useTable = (options: UseTableOptions) => {
  const {key, defaultPageSize = DEFAULT_PAGINATION.limit} = options;
  const dispatch = useDispatch();

  const currentPage = useSelector(selectCurrentPage(key));
  const pageSize = useSelector(selectPageSize(key)) || defaultPageSize;
  const sortConfig = useSelector(selectSortConfig(key));
  const filters = useSelector(selectFilters(key));

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(setCurrentPage({key, page}));
    },
    [dispatch, key]
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      dispatch(setPageSize({key, size}));
    },
    [dispatch, key]
  );

  const handleSort = useCallback(
    (sortKey: string) => {
      const currentSort = sortConfig;
      let direction: SortDirection = "asc";

      if (currentSort?.key === sortKey) {
        direction = currentSort.direction === "asc" ? "desc" : "asc";
      }

      dispatch(setSortConfig({key, sortKey, direction}));
    },
    [dispatch, key, sortConfig]
  );

  const clearSort = useCallback(() => {
    dispatch(clearSortConfig(key));
  }, [dispatch, key]);

  const handleFilterChange = useCallback(
    (filterKey: string, value: unknown) => {
      dispatch(updateFilter({key, filterKey, value}));
    },
    [dispatch, key]
  );

  const handleFiltersChange = useCallback(
    (newFilters: Record<string, unknown>) => {
      dispatch(setFilters({key, filters: newFilters}));
    },
    [dispatch, key]
  );

  const clearAllFilters = useCallback(() => {
    dispatch(clearFilters(key));
  }, [dispatch, key]);

  const clearSingleFilter = useCallback(
    (filterKey: string) => {
      dispatch(clearFilter({key, filterKey}));
    },
    [dispatch, key]
  );

  const queryParams = useMemo(() => {
    const params: Record<string, unknown> = {
      page: currentPage,
      limit: pageSize,
      ...filters,
    };

    if (sortConfig) {
      params.sort_by = sortConfig.key;
      params.sort_direction = sortConfig.direction;
    }

    return params;
  }, [currentPage, pageSize, filters, sortConfig]);

  const getQueryParams = useCallback(() => queryParams, [queryParams]);

  const getSortDirection = useCallback(
    (columnKey: string): SortDirection | undefined => {
      if (sortConfig?.key === columnKey) {
        return sortConfig.direction;
      }
      return undefined;
    },
    [sortConfig]
  );

  const isSorted = useCallback(
    (columnKey: string): boolean => {
      return sortConfig?.key === columnKey;
    },
    [sortConfig]
  );

  const isFiltered = useCallback(
    (filterKey?: string): boolean => {
      if (filterKey) {
        return filters[filterKey] !== undefined && filters[filterKey] !== "";
      }
      return Object.keys(filters).length > 0;
    },
    [filters]
  );

  const getFilterValue = useCallback(
    (filterKey: string) => {
      return filters[filterKey];
    },
    [filters]
  );

  return {
    // State
    currentPage,
    pageSize,
    sortConfig,
    filters,
    queryParams,

    // Actions
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    clearSort,
    handleFilterChange,
    handleFiltersChange,
    clearAllFilters,
    clearSingleFilter,

    // Helpers
    getQueryParams,
    getSortDirection,
    isSorted,
    isFiltered,
    getFilterValue,
  };
};
